import type { ProductResponse } from "@/app/api/types/product";

/**
 * SKU Prefix Index for O(1) lookups
 * Builds a map of SKU prefixes to products for instant filtering
 */
export class ProductSkuIndex {
  private exactMap: Map<string, ProductResponse> = new Map();
  private prefixMap: Map<string, ProductResponse[]> = new Map();
  private allProducts: ProductResponse[] = [];

  constructor(products: ProductResponse[]) {
    this.allProducts = products;
    this.buildIndex(products);
  }

  private buildIndex(products: ProductResponse[]) {
    for (const product of products) {
      const skuLower = product.sku.toLowerCase();

      // Store exact match
      this.exactMap.set(skuLower, product);

      // Store all prefixes (1 to full length)
      for (let i = 1; i <= skuLower.length; i++) {
        const prefix = skuLower.slice(0, i);
        const existing = this.prefixMap.get(prefix);
        if (existing) {
          existing.push(product);
        } else {
          this.prefixMap.set(prefix, [product]);
        }
      }
    }
  }

  /**
   * Fast SKU search using the index - O(1) for prefix lookups
   */
  searchBySku(searchTerm: string): ProductResponse[] {
    if (!searchTerm || searchTerm.trim() === "") {
      return this.allProducts;
    }

    const search = searchTerm.toLowerCase().trim();
    const exactMatches: ProductResponse[] = [];
    const prefixMatches: ProductResponse[] = [];
    const containsMatches: ProductResponse[] = [];
    const seenIds = new Set<string>();

    // Check exact match first - O(1)
    const exact = this.exactMap.get(search);
    if (exact) {
      exactMatches.push(exact);
      seenIds.add(exact.id);
    }

    // Get prefix matches - O(1) lookup
    const prefixResults = this.prefixMap.get(search);
    if (prefixResults) {
      for (const product of prefixResults) {
        if (!seenIds.has(product.id)) {
          // Check if it's exact or prefix
          if (product.sku.toLowerCase() === search) {
            // Already handled above
          } else {
            prefixMatches.push(product);
            seenIds.add(product.id);
          }
        }
      }
    }

    // For contains matches, we still need to scan (but this is less common)
    // Only do this if we have few results from prefix matching
    if (exactMatches.length + prefixMatches.length < 20) {
      for (const product of this.allProducts) {
        if (!seenIds.has(product.id)) {
          const skuLower = product.sku.toLowerCase();
          if (skuLower.includes(search)) {
            containsMatches.push(product);
            seenIds.add(product.id);
            // Limit contains matches to prevent slowdown
            if (containsMatches.length >= 10) break;
          }
        }
      }
    }

    return exactMatches.concat(prefixMatches, containsMatches);
  }
}

// Cache for the SKU index - keyed by products array reference
let cachedIndex: ProductSkuIndex | null = null;
let cachedProductsRef: ProductResponse[] | null = null;

/**
 * Get or create a SKU index for the given products array
 * Index is cached and reused if products array reference hasn't changed
 */
export function getProductSkuIndex(
  products: ProductResponse[],
): ProductSkuIndex {
  if (cachedProductsRef === products && cachedIndex) {
    return cachedIndex;
  }
  cachedIndex = new ProductSkuIndex(products);
  cachedProductsRef = products;
  return cachedIndex;
}

/**
 * Smart product filtering with SKU priority
 *
 * Filters products with intelligent ranking:
 * 1. Exact SKU matches (highest priority)
 * 2. SKU starts with search term
 * 3. SKU contains search term (only in name search mode)
 * 4. Exact code matches (for barcode scanners)
 * 5. Name/short_name matches (only in name search mode)
 *
 * @param products Array of products to filter
 * @param searchTerm Search query
 * @param isSkuMode Whether search is from SKU input (prefix-only matching for speed)
 * @returns Filtered and prioritized products
 */
export function filterProductsBySearch(
  products: ProductResponse[],
  searchTerm: string,
  isSkuMode: boolean = false,
): ProductResponse[] {
  if (!searchTerm || searchTerm.trim() === "") {
    return products;
  }

  // SKU MODE OPTIMIZATION: Use indexed lookups for O(1) performance
  if (isSkuMode) {
    const index = getProductSkuIndex(products);
    return index.searchBySku(searchTerm);
  }

  const search = searchTerm.toLowerCase().trim();

  // FULL SEARCH MODE (dropdown opened by button): Check all tiers
  // Group products into tiers during a single iteration
  const tiers: ProductResponse[][] = [[], [], [], [], [], []];
  const seenIds = new Set<string>();

  for (const product of products) {
    if (seenIds.has(product.id)) continue;

    const skuLower = product.sku.toLowerCase();
    const codeLower = product.code?.toLowerCase();
    const nameLower = product.name.toLowerCase();
    const shortNameLower = product.short_name?.toLowerCase();

    // Tier 0: Exact SKU matches (highest priority)
    if (skuLower === search) {
      tiers[0].push(product);
      seenIds.add(product.id);
      continue;
    }

    // Tier 1: SKU starts with search term (high priority)
    if (skuLower.startsWith(search)) {
      tiers[1].push(product);
      seenIds.add(product.id);
      continue;
    }

    // Tier 2: SKU contains search term (medium-high priority)
    if (skuLower.includes(search)) {
      tiers[2].push(product);
      seenIds.add(product.id);
      continue;
    }

    // Tier 3: Exact code matches (medium priority)
    if (codeLower && codeLower === search) {
      tiers[3].push(product);
      seenIds.add(product.id);
      continue;
    }

    // Tier 4: Code contains (medium-low priority)
    if (codeLower && codeLower.includes(search)) {
      tiers[4].push(product);
      seenIds.add(product.id);
      continue;
    }

    // Tier 5: Name/short_name matches (lower priority)
    if (
      nameLower.includes(search) ||
      (shortNameLower && shortNameLower.includes(search))
    ) {
      tiers[5].push(product);
      seenIds.add(product.id);
    }
  }

  // Flatten tiers into single result array (already in priority order)
  return tiers.flat();
}

/**
 * Check if a search term is likely a SKU (vs a product name)
 * Heuristic: SKUs are typically shorter, alphanumeric, may contain dashes/underscores
 */
export function likelyIsSku(searchTerm: string): boolean {
  if (!searchTerm) return false;

  const trimmed = searchTerm.trim();

  // Very short terms are likely SKUs
  if (trimmed.length <= 6) return true;

  // Contains mostly numbers/dashes/underscores
  const alphanumericRatio =
    trimmed.match(/[a-zA-Z0-9-_]/g)?.length || 0 / trimmed.length;
  if (alphanumericRatio > 0.8 && trimmed.length <= 12) return true;

  // Has typical SKU patterns (numbers with optional letters)
  if (/^\d+[A-Z]*$/.test(trimmed.toUpperCase())) return true;

  return false;
}

/**
 * Filter products for client-side search with smart caching
 * This version is optimized for instant results from cache
 */
export function filterCachedProducts(
  products: ProductResponse[],
  searchTerm: string,
  options: {
    isSkuMode?: boolean;
    includeSleeping?: boolean;
    excludeVariants?: boolean;
  } = {},
): ProductResponse[] {
  const {
    isSkuMode = false,
    includeSleeping = false,
    excludeVariants = false,
  } = options;

  let filtered = products;

  // Apply sleeping filter
  if (!includeSleeping) {
    filtered = filtered.filter((p) => !p.is_sleeping);
  }

  // Apply variants filter
  // Note: parent_id doesn't exist in ProductResponse
  // Variants are handled through children relationships
  if (excludeVariants) {
    filtered = filtered.filter((p) => !p.children || p.children.length === 0);
  }

  // Apply search filter with priority
  if (searchTerm && searchTerm.trim()) {
    filtered = filterProductsBySearch(filtered, searchTerm, isSkuMode);
  }

  return filtered;
}
